import { queryOptions, type Query, type QueryClient } from "@tanstack/react-query";
import type { FormWorkflowService } from "@/services/form-workflow.service";
import type {
  TFormWorkflow,
  TWorkflowAvailableField,
  TWorkflowLookups,
  TWorkflowStatusSource,
} from "@/types/form-workflow";
import { FRESHNESS } from "./policies";
import { scopedKey, scopedRead, type QueryScope } from "./scope";

/**
 * An intake form's workflow draft and the lookups its builder labels with
 * (NG-2809). Only the builder's first read moves here: `FormWorkflowStore`
 * stays the draft engine, seeded once per open from a read made for that open,
 * and owns every write after it.
 *
 * - **draft** `[slug, uid, 'form-workflow', formId, 'draft']`: the GET also
 *   get-or-creates the draft for an editor, and its body is redacted to what
 *   the caller may read, so it is access-sensitive. A reused answer must never
 *   seed the working copy (autosave PUTs it back), so the builder reads it on
 *   every open.
 * - **lookups** `[slug, uid, 'form-workflow', formId, 'lookups']`: assignee
 *   and status labels for the canvas. Reference data.
 *
 * Both are plain GETs, so both declare `"safe-read"`.
 *
 * @writers None: the draft engine's saves, publishes and resets answer with the
 * draft they wrote and seat it in the store, which owns the working copy from
 * the first seat on. Nothing reads the cached draft after that.
 */

/** `[slug, uid, 'form-workflow', formId, 'draft']`. */
function formWorkflowDraftKey(scope: QueryScope, formId: string) {
  return scopedKey(scope, "form-workflow", formId, "draft");
}

/** `[slug, uid, 'form-workflow', formId, 'lookups']`. */
function formWorkflowLookupsKey(scope: QueryScope, formId: string) {
  return scopedKey(scope, "form-workflow", formId, "lookups");
}

export function formWorkflowDraftQuery(
  service: Pick<FormWorkflowService, "loadWorkflow">,
  scope: QueryScope,
  formId: string,
) {
  return queryOptions({
    ...FRESHNESS["access-sensitive"],
    queryKey: formWorkflowDraftKey(scope, formId),
    queryFn: scopedRead(
      (request): Promise<TFormWorkflow> => service.loadWorkflow(scope.tenant, formId, request),
      { retry: "safe-read" },
    ),
  });
}

export function formWorkflowLookupsQuery(
  service: Pick<FormWorkflowService, "loadLookups">,
  scope: QueryScope,
  formId: string,
) {
  return queryOptions({
    ...FRESHNESS.reference,
    queryKey: formWorkflowLookupsKey(scope, formId),
    queryFn: scopedRead(
      (request): Promise<TWorkflowLookups> => service.loadLookups(scope.tenant, formId, request),
      { retry: "safe-read" },
    ),
  });
}

/**
 * What the graph says about one step of the draft (NG-3381): the fields on the
 * Pages that precede it (`available-fields`, the Branch condition, Change Owner
 * and `visible_if` source pickers) and whether its Status is inherited from a
 * Review lane (`status-source`). One entry per step, shared by every panel that
 * shows it.
 *
 * - **graph** `[slug, uid, 'form-workflow', formId, 'graph', elementId, part]`.
 *
 * The server answers from the SAVED draft, so the read is the store's
 * (`FormWorkflowStore.loadAvailableFields` / `loadStatusSource`): it waits on
 * the pending autosave first (NG-81, NG-878). The builder passes it in as
 * `load`, so a view-only caller still skips that save.
 *
 * Freshness: mutable, with no poll and no focus or reconnect re-read. The answer
 * changes only when the graph does, and every graph change marks these stale
 * (below), so a panel that opens on a step re-reads it while one already open
 * keeps the answer it opened with, as before. A re-read on every edit would be
 * a request per keystroke in the panel. No retry: the read follows a PUT.
 *
 * @writers `FormWorkflowStore`: every graph edit (`markDirty`) and every draft
 * it seats from the server (load, save, Page save, publish, discard, reset)
 * mark the graph entries stale with `markCachedWorkflowGraphStale`.
 */

const GRAPH_POLICY = {
  ...FRESHNESS.mutable,
  refetchInterval: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} as const;

type GraphPart = "available-fields" | "status-source";

/** `[slug, uid, 'form-workflow', formId, 'graph', elementId, part]`. */
function workflowGraphKey(scope: QueryScope, formId: string, elementId: string, part: GraphPart) {
  return scopedKey(scope, "form-workflow", formId, "graph", elementId, part);
}

/** The fields on the Pages before `elementId`, read through `load`. */
export function workflowAvailableFieldsQuery(
  load: (elementId: string) => Promise<TWorkflowAvailableField[]>,
  scope: QueryScope,
  formId: string,
  elementId: string,
) {
  return queryOptions({
    ...GRAPH_POLICY,
    queryKey: workflowGraphKey(scope, formId, elementId, "available-fields"),
    queryFn: scopedRead(() => load(elementId)),
  });
}

/** Whether `elementId`'s Status comes from a Review lane, read through `load`. */
export function workflowStatusSourceQuery(
  load: (elementId: string) => Promise<TWorkflowStatusSource>,
  scope: QueryScope,
  formId: string,
  elementId: string,
) {
  return queryOptions({
    ...GRAPH_POLICY,
    queryKey: workflowGraphKey(scope, formId, elementId, "status-source"),
    queryFn: scopedRead(() => load(elementId)),
  });
}

/** A graph entry of any form, tenant or principal. */
function isWorkflowGraphQuery(query: Query): boolean {
  const key = query.queryKey;
  return key[2] === "form-workflow" && key[4] === "graph";
}

/**
 * Mark every cached graph answer stale, without re-reading any: the next panel
 * to show a step reads it again, and one already open keeps its answer. Only
 * one builder is open at a time, so this does not narrow to a form.
 */
export function markCachedWorkflowGraphStale(client: QueryClient): void {
  client
    .invalidateQueries({ predicate: isWorkflowGraphQuery, refetchType: "none" })
    .catch((error: unknown) => {
      console.warn("[form-workflow] marking the graph reads stale failed", { error });
    });
}
